// Live's subtitle importers (#239, spec docs/specs/editor/captions.md, "Import" and decision 6):
// a .srt, .vtt, .ass/.ssa or .lrc file read into timed cues, and the cues' words, each cue's time
// shared evenly word by word. Live's rules, with the fixes decision 6 lists: an SRT or VTT time may
// leave out its hours and its fraction is a decimal, every VTT NOTE, STYLE and REGION block is
// dropped, an ASS file's own `Format:` line names its fields, `\n` and `\h` become spaces, an LRC's
// `[offset:]` is applied and its word stamps dropped, and cues sort stably. Pure.
import { MAX_VIDEO_MS, plainText, wordsIn, type TimedWord } from "./words";

/** The largest file the importer reads: far past 5000 words, small enough to read at once. */
export const MAX_SUBTITLE_BYTES = 2 * 1024 * 1024;

export type SubtitleFormat = "srt" | "vtt" | "ass" | "lrc";

/** A cue: its text shown from `startMs` to `endMs` (whole ms from the video's start). */
export interface Cue {
  startMs: number;
  endMs: number;
  text: string;
}

/** The accept list for the file input: ".srt,.ass,.ssa,.vtt,.lrc" */
export const SUBTITLE_ACCEPT = ".srt,.ass,.ssa,.vtt,.lrc";

/** The format each extension names; an SSA file is read as ASS (live's). A map, not an object, so
 * an extension such as `constructor` names nothing. */
const FORMAT_OF_EXTENSION: ReadonlyMap<string, SubtitleFormat> = new Map([
  ["srt", "srt"],
  ["vtt", "vtt"],
  ["ass", "ass"],
  ["ssa", "ass"],
  ["lrc", "lrc"],
]);

/** The format a file's name says (its extension, any case: .srt, .vtt, .ass, .ssa (as ass), .lrc), or null. */
export function formatOf(fileName: string): SubtitleFormat | null {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return null;
  return FORMAT_OF_EXTENSION.get(fileName.slice(dot + 1).toLowerCase()) ?? null;
}

/** Each format's reader, from the file's lines to its cues (text cleaned, empty ones included). */
const CUES_OF: Record<SubtitleFormat, (lines: readonly string[]) => Cue[]> = {
  srt: srtCues,
  vtt: vttCues,
  ass: assCues,
  lrc: lrcCues,
};

/** The file's cues, sorted by start; none when it holds no timed cue. Text cleaned (below). */
export function parseSubtitles(format: SubtitleFormat, content: string): Cue[] {
  const lines = linesOf(content);
  // A cue at or past 300 s (the longest video) is dropped before the sort.
  const cues = CUES_OF[format](lines).filter((cue) => cue.text !== "" && cue.startMs < MAX_VIDEO_MS);
  // Array sort is stable: cues starting together keep the file's order. Overlaps stay as they are.
  return cues.sort((a, b) => a.startMs - b.startMs);
}

/** The cues' words, each cue's time shared evenly word by word (live's rule), in time order. */
export function wordsOfCues(cues: readonly Cue[]): TimedWord[] {
  return cues.flatMap(wordsOfCue).filter((word) => word.startMs < MAX_VIDEO_MS);
}

/**
 * A cue's words, its time cut into equal shares, one a word. The cuts are rounded where they fall,
 * so the words tile the cue: the first starts at its start, the last ends at its end, and each
 * ends where the next starts. A cue that ends at or before its start gives its words no length.
 */
function wordsOfCue(cue: Cue): TimedWord[] {
  const texts = wordsIn(cue.text);
  const length = Math.max(0, cue.endMs - cue.startMs);
  const cut = (index: number) => Math.max(0, Math.round(cue.startMs + (index * length) / texts.length));
  return texts.map((text, index) => ({ text, startMs: cut(index), endMs: cut(index + 1) }));
}

// ---------------------------------------------------------------------------------------------
// Shared: lines, times and text.

/** The file's lines: a leading byte-order mark dropped, and `\r\n` (or a lone `\r`) read as `\n`. */
function linesOf(content: string): string[] {
  return content.replace(/^﻿/, "").split(/\r\n?|\n/);
}

/**
 * A subtitle time, `[H:]MM:SS` and an optional fraction after `,` or `.`, read as a decimal
 * fraction of a second (`,5`, `,50` and `,500` are all half a second; ASS's centiseconds too).
 * Up to nine digits in each part, and no range check, as live's.
 */
// At most nine digits a part: an unbounded run of digits next to `-->` reads in quadratic time.
const TIME = String.raw`(?:(\d{1,9}):)?(\d{1,9}):(\d{1,9})(?:[,.](\d{1,9}))?`;
/** The first time anywhere in a text (each side of an SRT or VTT `-->`). */
const TIME_IN = new RegExp(TIME);
/** A text that is a time and nothing else (an ASS Start or End field). */
const TIME_ONLY = new RegExp(`^${TIME}$`);

/** The time a match of `TIME` reads, in whole milliseconds. */
function msOfTime(match: RegExpExecArray): number {
  const [, hours = "0", minutes = "0", seconds = "0", fraction = ""] = match;
  const whole = (Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds)) * 1000;
  return whole + fractionMs(fraction);
}

/** The digits after a decimal point, in whole milliseconds: "5" and "500" are both 500. */
function fractionMs(digits: string): number {
  return digits === "" ? 0 : Math.round(Number(`0.${digits}`) * 1000);
}

/**
 * A cue's text as shown: every `<...>` tag (HTML and VTT tags, VTT and enhanced-LRC time stamps)
 * and every `{...}` block (ASS overrides) removed, ASS's `\N`, `\n` and `\h` read as spaces, and
 * every run of white space one space, trimmed.
 */
function cleanText(text: string): string {
  // A tag or a block holds no `<` or `{` of its own, so a run of them reads in linear time; the
  // server's blank characters (U+0085 among them) are one space, control characters gone.
  return plainText(
    text
      .replace(/<[^<>]*>/g, "")
      .replace(/\{[^{}]*\}/g, "")
      .replace(/\\[Nnh]/g, " "),
  );
}

// ---------------------------------------------------------------------------------------------
// SRT and VTT.

/** The file's blocks: runs of lines between blank lines (a line of only white space is blank). */
function blocksOf(lines: readonly string[]): string[][] {
  const blocks: string[][] = [];
  let block: string[] = [];
  for (const line of lines) {
    if (line.trim() !== "") {
      block.push(line);
    } else if (block.length > 0) {
      blocks.push(block);
      block = [];
    }
  }
  if (block.length > 0) blocks.push(block);
  return blocks;
}

/**
 * A block's cue: its first line holding `-->` is the timing, the lines before it (a cue number or a
 * VTT cue identifier) are ignored, and the lines after it are the text, joined by a space. The
 * start is the first time before the arrow, the end the first after it; what follows the end (VTT
 * cue settings) is ignored. No timing line, or a time that does not read: no cue.
 */
function cueOfBlock(block: readonly string[]): Cue | null {
  const timing = block.findIndex((line) => line.includes("-->"));
  if (timing < 0) return null;
  const line = block[timing]!;
  const arrow = line.indexOf("-->");
  const start = TIME_IN.exec(line.slice(0, arrow));
  const end = TIME_IN.exec(line.slice(arrow + 3));
  if (start === null || end === null) return null;
  const text = cleanText(block.slice(timing + 1).join(" "));
  return { startMs: msOfTime(start), endMs: msOfTime(end), text };
}

function srtCues(lines: readonly string[]): Cue[] {
  return blocksOf(lines).flatMap((block) => cueOfBlock(block) ?? []);
}

/** A VTT block that holds no cue: a comment, a style sheet or a region (by its first line). */
const VTT_NOT_A_CUE = /^(?:NOTE|STYLE|REGION)(?:[ \t]|$)/;

/** A VTT file: its `WEBVTT` header block and every NOTE, STYLE and REGION block dropped, then the
 * rest read as SRT blocks. */
function vttCues(lines: readonly string[]): Cue[] {
  return blocksOf(lines)
    .filter((block, index) => !(index === 0 && block[0]!.startsWith("WEBVTT")))
    .filter((block) => !VTT_NOT_A_CUE.test(block[0]!))
    .flatMap((block) => cueOfBlock(block) ?? []);
}

// ---------------------------------------------------------------------------------------------
// ASS and SSA.

/** Where a Dialogue line's Start, End and Text are, and how many comma-separated fields it has. */
interface AssFields {
  start: number;
  end: number;
  text: number;
  count: number;
}

/** The standard order (ASS's Layer or SSA's Marked first), for a file with no usable Format line. */
const STANDARD_ASS_FORMAT = ["layer", "start", "end", "style", "name", "marginl", "marginr", "marginv", "effect", "text"];

/** The fields a Format line names, or null when it does not name Start, End and Text. */
function assFieldsOf(names: readonly string[]): AssFields | null {
  const start = names.indexOf("start");
  const end = names.indexOf("end");
  const text = names.indexOf("text");
  if (start < 0 || end < 0 || text < 0) return null;
  return { start, end, text, count: names.length };
}

/** The `[Events]` section's `Format:` line's fields (the Styles section has its own, not this). */
function eventsFormatOf(lines: readonly string[]): AssFields | null {
  let section = "";
  for (const line of lines) {
    const header = /^\[(.+)\]$/.exec(line.trim());
    if (header !== null) section = header[1]!.toLowerCase();
    else if (section === "events" && line.startsWith("Format:")) {
      return assFieldsOf(line.slice("Format:".length).split(",").map((name) => name.trim().toLowerCase()));
    }
  }
  return null;
}

/** A line cut on its commas into `count` fields, the last keeping the rest (the Text's commas);
 * null when it has fewer. */
function splitFields(line: string, count: number): string[] | null {
  const fields: string[] = [];
  let from = 0;
  for (let index = 0; index < count - 1; index++) {
    const comma = line.indexOf(",", from);
    if (comma < 0) return null;
    fields.push(line.slice(from, comma));
    from = comma + 1;
  }
  fields.push(line.slice(from));
  return fields;
}

/** A `Dialogue:` line's cue, or null when it has too few fields or a time does not read. */
function assCueOf(line: string, fields: AssFields): Cue | null {
  const values = splitFields(line.slice("Dialogue:".length), fields.count);
  if (values === null) return null;
  const start = TIME_ONLY.exec(values[fields.start]!.trim());
  const end = TIME_ONLY.exec(values[fields.end]!.trim());
  if (start === null || end === null) return null;
  return { startMs: msOfTime(start), endMs: msOfTime(end), text: cleanText(values[fields.text]!) };
}

/** An ASS or SSA file: only `Dialogue:` lines (case-sensitive, at the line's start) are cues;
 * `Comment:` lines and the other sections are not. */
function assCues(lines: readonly string[]): Cue[] {
  const fields = eventsFormatOf(lines) ?? assFieldsOf(STANDARD_ASS_FORMAT)!;
  return lines.filter((line) => line.startsWith("Dialogue:")).flatMap((line) => assCueOf(line, fields) ?? []);
}

// ---------------------------------------------------------------------------------------------
// LRC.

/** A line's time stamp: `[m:ss]`, `[mm:ss.xx]` or `[mm:ss:xx]`, the fraction a decimal. */
const LRC_STAMP = /\[(\d{1,3}):(\d{1,2})(?:[.:](\d{1,3}))?\]/g;
/** A metadata tag, `[key: value]`, on a line with no time stamp. */
const LRC_TAG = /^\[(\w{1,6}):(.*)\]$/;
/** `[length:]`'s value, `mm:ss` or `mm:ss.xx`. */
const LRC_LENGTH = /^(\d+):(\d{1,2})(?:\.(\d{1,3}))?$/;
/** `[offset:]`'s value, whole milliseconds, signed. */
const LRC_OFFSET = /^[+-]?\d+$/;
/** How long the last line shows when the file gives no later `[length:]`. */
const LRC_LAST_LINE_MS = 5000;

/** A stamped line's entry: when it starts, and its text (empty for a bare stamp ending a line). */
interface LrcEntry {
  startMs: number;
  text: string;
}

/** A line's entries, one per stamp anywhere on it, each with the text after the last stamp (a
 * repeated chorus); none when it has no stamp. */
function entriesOfLine(line: string): LrcEntry[] {
  const stamps = [...line.matchAll(LRC_STAMP)];
  const last = stamps.at(-1);
  if (last === undefined) return [];
  const text = cleanText(line.slice(last.index + last[0].length));
  return stamps.map(([, minutes = "0", seconds = "0", fraction = ""]) => ({
    startMs: (Number(minutes) * 60 + Number(seconds)) * 1000 + fractionMs(fraction),
    text,
  }));
}

/** The metadata tags on lines with no stamp, keys lower-cased; empty values are left out. */
function tagsOf(lines: readonly string[]): Map<string, string> {
  const tags = new Map<string, string>();
  for (const line of lines) {
    const tag = LRC_TAG.exec(line);
    const value = tag?.[2]?.trim() ?? "";
    if (tag !== null && value !== "" && entriesOfLine(line).length === 0) tags.set(tag[1]!.toLowerCase(), value);
  }
  return tags;
}

/** `[length:]` in milliseconds, or null when there is none or it does not read. */
function lengthMsOf(tags: ReadonlyMap<string, string>): number | null {
  const length = LRC_LENGTH.exec(tags.get("length") ?? "");
  if (length === null) return null;
  const [, minutes = "0", seconds = "0", fraction = ""] = length;
  return (Number(minutes) * 60 + Number(seconds)) * 1000 + fractionMs(fraction);
}

/** `[offset:]` in milliseconds; a positive offset shows the lyrics earlier (the LRC convention). */
function offsetMsOf(tags: ReadonlyMap<string, string>): number {
  const offset = tags.get("offset") ?? "";
  return LRC_OFFSET.test(offset) ? Number(offset) : 0;
}

/**
 * An LRC file: every stamped line's entries, moved by `[offset:]` (never before 0) and sorted by
 * time. Each ends where the next starts; the last at `[length:]` when that is later than its
 * start, else 5 s after it. An entry with no text gives no cue (the caller drops it) but still ends
 * the one before. `[length:]` is the audio's length, so the offset does not move it.
 */
function lrcCues(lines: readonly string[]): Cue[] {
  const trimmed = lines.map((line) => line.trim());
  const tags = tagsOf(trimmed);
  const offsetMs = offsetMsOf(tags);
  const entries = trimmed
    .flatMap(entriesOfLine)
    .map((entry) => ({ ...entry, startMs: Math.max(0, entry.startMs - offsetMs) }))
    .sort((a, b) => a.startMs - b.startMs);
  const lengthMs = lengthMsOf(tags);
  return entries.map((entry, index) => {
    const next = entries[index + 1];
    const lastEndMs = lengthMs !== null && lengthMs > entry.startMs ? lengthMs : entry.startMs + LRC_LAST_LINE_MS;
    return { startMs: entry.startMs, endMs: next?.startMs ?? lastEndMs, text: entry.text };
  });
}
